
import { stripHTML } from "@decocms/apps-website/utils/html"
import type { ImageWidget } from "~/types/widgets"
import { SITE } from "~/constants"

export const seo = true;


export type OGType = 'website' | 'article' | 'og:product'

export interface Props {
    title?: string
    /**
     * @title Template do titulo
     * @description %s - Miess
     */
    titleTemplate?: string
    description?: string
    /**
     * @title Template da descrição
     * @description %s - Miess
     */
    descriptionTemplate?: string
    /** @default website */
    type?: OGType
    /** @description Tamanho recomendado: 1200 x 630 px (até 5MB) */
    jsonLDs?: string[]
    /**
     * @title Prefix da <head>
     */
    prefix?: string
    image?: ImageWidget
    /** @description Tamanho recomendado: 16 x 16 px */
    favicon?: ImageWidget
    /** @title Canonical URL */
    canonical?: string
    /**
     * @title Desabilitar indexação (noindex, nofollow)
     */
    noIndexing?: boolean
    /**
     * @title Desabilitar indexação (noindex)
     */
    onlyNoIndex?: boolean
    /**
     * @title Desabilitar dados estruturados (JSON-LD)
     */
    noJsonLD?: boolean
    /**
     * @ignore
     */
    customOGDescription?: string
    /**
     * @ignore
     */
    customOGTitle?: string
    /**
     * @ignore
     */
    url?: string
    /**
     * @ignore
     */
    keywords?: string
    storeURL?: string
    dynamicCanonical?: string
}

/**
 * Assemble the head payload (title/meta/link/OG/Twitter) that used to be
 * rendered by this component. Emitted once by the route `head()` via
 * ~/components/seo/seoHead.ts. `robots` overrides the noIndexing/onlyNoIndex
 * derivation (used by CustomSEOPLP for its noindex/nofollow CSV rules).
 */
export function buildSeoHeadPayload(
    props: Props & { dynamicCanonical?: string; robots?: string },
) {
    const title = (props.titleTemplate ?? '%s').replace('%s', stripHTML(props.title ?? ''))
    const description = (props.descriptionTemplate ?? '%s').replace(
        '%s',
        stripHTML(props.description ?? ''),
    )
    const robots = props.robots ??
        (props.noIndexing ? 'noindex, nofollow' : props.onlyNoIndex ? 'noindex' : undefined)
    const twitterCard = props.type === 'website' ? 'summary' : 'summary_large_image'
    const canonical = props.canonical || props.dynamicCanonical

    return {
        title,
        meta: [
            { name: 'description', content: description },
            ...(robots ? [{ name: 'robots', content: robots }] : []),
            ...(props.keywords ? [{ name: 'keywords', content: props.keywords }] : []),
        ],
        link: [
            ...(props.favicon ? [{ rel: 'icon', href: props.favicon }] : []),
            ...(canonical ? [{ rel: 'canonical', href: canonical }] : []),
        ],
        opengraph: [
            { property: 'og:title', content: props.customOGTitle ?? title },
            { property: 'og:description', content: props.customOGDescription ?? description },
            ...(props.type ? [{ property: 'og:type', content: props.type }] : []),
            ...(props.image ? [{ property: 'og:image', content: props.image }] : []),
            ...(props.url ? [{ property: 'og:url', content: props.url }] : []),
        ],
        twitter: [
            { property: 'twitter:title', content: props.customOGTitle ?? title },
            {
                property: 'twitter:description',
                content: props.customOGDescription ?? description,
            },
            ...(props.image ? [{ property: 'twitter:image', content: props.image }] : []),
            { property: 'twitter:card', content: twitterCard },
        ],
    }
}

export function loader(props: Props & { __pageUrl?: string }) {
    const url = new URL(props.__pageUrl ?? "/", SITE.URL)
    const storeURL = url.origin
    const dynamicCanonical = `${storeURL}${url.pathname.toLowerCase()}`

    return {
        ...props,
        storeURL,
        dynamicCanonical,
        ...buildSeoHeadPayload({ ...props, dynamicCanonical }),
    }
}

// The head tags (title/description/robots/OG/Twitter/canonical) are emitted
// once by the route `head()` from this section's loader output (see
// ~/components/seo/seoHead.ts). Rendering them here as well duplicated every
// tag in <head> (DECO-5439/5440/5444). Only JSON-LD stays section-rendered.
function Component({ jsonLDs, noJsonLD }: Props) {
    return (
        <>
            {!noJsonLD &&
                jsonLDs?.map((json) => (
                    <script
                        type='application/ld+json'
                        dangerouslySetInnerHTML={{ __html: json }}
                    />
                ))}
        </>
    )
}

export default Component
